import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BaseService} from 'public-shared/services/base-http/base.service';
import {Observable} from 'rxjs';
import {DataRetentionBackfillEndpoints} from 'shared/services/endpoint-constants';
import {
  IBackfillJobListResponse,
  IBackfillJobRequest,
  IDataRetentionBackfillJob,
  IDataRetentionBackfillJobPartsResponse
} from './data-retention-backfill.dto';

/**
 * Handles the Data Retention Backfill API requests (wipeout-service backfill admin API).
 *
 * These endpoints live under `/api/v1/merchants/dataretention/backfill/...`, so this
 * service keeps the default empty `baseUrl` and passes the full relative path to the
 * HTTP helpers.
 */
@Injectable()
export class DataRetentionBackfillService extends BaseService {
  constructor(protected override http: HttpClient) {
    super(http);
  }

  /**
   * Creates a backfill job.
   * @param {IBackfillJobRequest} request The job creation payload.
   * @returns {Observable<IDataRetentionBackfillJob>} The created job in an Observable.
   */
  public createJob$(request: IBackfillJobRequest): Observable<IDataRetentionBackfillJob> {
    return this.post$<IDataRetentionBackfillJob>(DataRetentionBackfillEndpoints.JOBS, request);
  }

  /**
   * Returns a merchant's paginated backfill jobs (newest first).
   * @param {string} merchantGuid The merchant's public id (guid).
   * @param {number} page The 0-based page index.
   * @param {number} size The page size.
   * @param {string} [status] Optional status filter.
   * @returns {Observable<IBackfillJobListResponse>} The result in an Observable.
   */
  public getJobs$(merchantGuid: string, page: number, size: number, status?: string): Observable<IBackfillJobListResponse> {
    return this.get$<IBackfillJobListResponse>(
      DataRetentionBackfillEndpoints.JOBS_BY_MERCHANT(merchantGuid),
      undefined,
      this.buildPageParams(page, size, status)
    );
  }

  /**
   * Returns a single backfill job.
   * @param {string} jobId The job's id.
   * @returns {Observable<IDataRetentionBackfillJob>} The result in an Observable.
   */
  public getJob$(jobId: string): Observable<IDataRetentionBackfillJob> {
    return this.get$<IDataRetentionBackfillJob>(DataRetentionBackfillEndpoints.JOB(jobId));
  }

  /**
   * Returns a job's paginated parts, along with the parent job metadata.
   * @param {string} jobId The job's id.
   * @param {number} page The 0-based page index.
   * @param {number} size The page size.
   * @returns {Observable<IDataRetentionBackfillJobPartsResponse>} The result in an Observable.
   */
  public getJobParts$(jobId: string, page: number, size: number): Observable<IDataRetentionBackfillJobPartsResponse> {
    return this.get$<IDataRetentionBackfillJobPartsResponse>(
      DataRetentionBackfillEndpoints.JOB_PARTS(jobId),
      undefined,
      this.buildPageParams(page, size)
    );
  }

  /**
   * Pauses a running backfill job.
   * @param {string} jobId The job's id.
   * @returns {Observable<void>} An empty Observable.
   */
  public pauseJob$(jobId: string): Observable<void> {
    return this.post$<void>(DataRetentionBackfillEndpoints.JOB_PAUSE(jobId), {});
  }

  /**
   * Resumes a paused backfill job.
   * @param {string} jobId The job's id.
   * @returns {Observable<void>} An empty Observable.
   */
  public resumeJob$(jobId: string): Observable<void> {
    return this.post$<void>(DataRetentionBackfillEndpoints.JOB_RESUME(jobId), {});
  }

  /**
   * Cancels a backfill job.
   * @param {string} jobId The job's id.
   * @returns {Observable<void>} An empty Observable.
   */
  public cancelJob$(jobId: string): Observable<void> {
    return this.post$<void>(DataRetentionBackfillEndpoints.JOB_CANCEL(jobId), {});
  }

  /**
   * Builds the `page`/`size` (and optional `status`) query params shared by the
   * listing endpoints.
   */
  private buildPageParams(page: number, size: number, status?: string): HttpParams {
    let params = new HttpParams().set('page', page).set('size', size);
    if (status) {
      params = params.set('status', status);
    }
    return params;
  }
}
